import type {
  RequestLatencyDistribution,
  WorkloadMetricsEntry,
  WorkloadMetricsInterval,
} from '@/lib/urm-api';
import { LATENCY_BUCKETS, type LatencyBucketKey } from './latency.logic';

export const MIN_STABILITY_REQUESTS_PER_INTERVAL = 20;
export const MIN_STABILITY_ELIGIBLE_INTERVALS = 10;
export const MIN_STABILITY_MEASURED_REQUESTS = 200;

export type LatencyStabilityRating = 'stable' | 'mixed' | 'variable' | 'insufficient';
export type LatencyStabilityConfidence = 'high' | 'medium' | 'low';
/** Which percentile's drift pushed the rating off 'stable'. */
export type LatencyStabilityDriver = 'median' | 'tail' | 'both';

/** Stable is best, insufficient is worst; higher means less stable. */
export const RATING_ORDER: Record<LatencyStabilityRating, number> = {
  stable: 0,
  mixed: 1,
  variable: 2,
  insufficient: 3,
};

export interface LatencyStabilityPoint {
  timestamp: number;
  p50Ms: number;
  p50Censored: boolean;
  p95Ms: number;
  censored: boolean;
  measuredRequests: number;
}

export interface LatencyStabilitySummary {
  key: string;
  label: string;
  resourceType: string;
  action: string;
  apiLabel: string;
  rating: LatencyStabilityRating;
  confidence: LatencyStabilityConfidence;
  spreadDriver: LatencyStabilityDriver;
  typicalP95Ms: number | null;
  typicalP95Censored: boolean;
  p10P95Ms: number | null;
  p90P95Ms: number | null;
  temporalSpreadRatio: number | null;
  temporalSpreadIsLowerBound: boolean;
  medianSpreadRatio: number | null;
  typicalP50Ms: number | null;
  windowP50Ms: number | null;
  windowP95Ms: number | null;
  windowP95Censored: boolean;
  tailSpreadRatio: number | null;
  measuredRequests: number;
  totalRequests: number;
  histogramCoveragePercent: number;
  activeIntervals: number;
  eligibleIntervals: number;
  eligibleIntervalCoveragePercent: number;
  censoredIntervals: number;
  slowerThan1000Percent: number | null;
  points: LatencyStabilityPoint[];
}

interface QuantileEstimate {
  value: number;
  censored: boolean;
}

interface MutableGroup {
  sample: WorkloadMetricsEntry;
  totalRequests: number;
  measuredRequests: number;
  activeIntervals: number;
  eligibleIntervals: number;
  censoredIntervals: number;
  counts: RequestLatencyDistribution;
  points: LatencyStabilityPoint[];
}

function emptyDistribution(): RequestLatencyDistribution {
  return {
    lt_25_ms: 0,
    '25_to_50_ms': 0,
    '50_to_100_ms': 0,
    '100_to_250_ms': 0,
    '250_to_500_ms': 0,
    '500_to_1000_ms': 0,
    '1000_to_2500_ms': 0,
    '2500_to_5000_ms': 0,
    gte_5000_ms: 0,
  };
}

function validCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function addDistribution(
  target: RequestLatencyDistribution,
  source: RequestLatencyDistribution,
): void {
  for (const bucket of LATENCY_BUCKETS) {
    target[bucket.key] += validCount(source[bucket.key]);
  }
}

function distributionTotal(distribution: RequestLatencyDistribution): number {
  return LATENCY_BUCKETS.reduce(
    (total, bucket) => total + validCount(distribution[bucket.key]),
    0,
  );
}

function estimateHistogramQuantile(
  distribution: RequestLatencyDistribution,
  percentile: number,
): QuantileEstimate | null {
  const total = distributionTotal(distribution);
  if (total === 0) return null;

  const rank = Math.min(1, Math.max(0, percentile)) * total;
  let cumulative = 0;
  for (const bucket of LATENCY_BUCKETS) {
    const count = validCount(distribution[bucket.key]);
    if (count === 0) continue;
    if (cumulative + count >= rank) {
      if (bucket.upperMs === null) return { value: bucket.lowerMs, censored: true };
      const fraction = Math.min(1, Math.max(0, (rank - cumulative) / count));
      return {
        value: bucket.lowerMs + fraction * (bucket.upperMs - bucket.lowerMs),
        censored: false,
      };
    }
    cumulative += count;
  }
  return null;
}

function percentilePoint(
  points: LatencyStabilityPoint[],
  percentile: number,
  field: 'p50Ms' | 'p95Ms' = 'p95Ms',
): QuantileEstimate | null {
  if (points.length === 0) return null;
  const censoredField = field === 'p50Ms' ? 'p50Censored' : 'censored';
  const sorted = [...points].sort((left, right) => left[field] - right[field]);
  const position = Math.min(1, Math.max(0, percentile)) * (sorted.length - 1);
  const lower = sorted[Math.floor(position)];
  const upper = sorted[Math.ceil(position)];
  const fraction = position - Math.floor(position);
  return {
    value: lower[field] + fraction * (upper[field] - lower[field]),
    censored: lower[censoredField] || upper[censoredField],
  };
}

function entryKey(entry: WorkloadMetricsEntry): string {
  return JSON.stringify([entry.resource_type, entry.action.toLowerCase(), entry.api_label]);
}

function ratingFor(
  spread: number | null,
  spreadIsLowerBound: boolean,
  eligibleIntervals: number,
  measuredRequests: number,
): LatencyStabilityRating {
  if (eligibleIntervals < MIN_STABILITY_ELIGIBLE_INTERVALS
    || measuredRequests < MIN_STABILITY_MEASURED_REQUESTS
    || spread === null) {
    return 'insufficient';
  }
  // A censored upper percentile only gives us a lower bound. It is safe to
  // call it variable when that lower bound already exceeds the threshold, but
  // never safe to call it stable or mixed.
  if (spreadIsLowerBound) return spread > 2 ? 'variable' : 'insufficient';
  if (spread <= 1.25) return 'stable';
  if (spread <= 2) return 'mixed';
  return 'variable';
}

function confidenceFor(
  rating: LatencyStabilityRating,
  eligibleIntervals: number,
  measuredRequests: number,
  eligibleCoveragePercent: number,
  histogramCoveragePercent: number,
  hasDroppedRecords: boolean,
): LatencyStabilityConfidence {
  if (rating === 'insufficient') return 'low';
  if (eligibleIntervals >= 30
    && measuredRequests >= 1_000
    && eligibleCoveragePercent >= 80
    && histogramCoveragePercent >= 80
    && !hasDroppedRecords) {
    return 'high';
  }
  return 'medium';
}

function intervalGroups(entries: WorkloadMetricsEntry[]): Map<string, {
  sample: WorkloadMetricsEntry;
  totalRequests: number;
  counts: RequestLatencyDistribution;
}> {
  const groups = new Map<string, {
    sample: WorkloadMetricsEntry;
    totalRequests: number;
    counts: RequestLatencyDistribution;
  }>();
  for (const entry of entries) {
    const key = entryKey(entry);
    const group = groups.get(key) ?? {
      sample: entry,
      totalRequests: 0,
      counts: emptyDistribution(),
    };
    group.totalRequests += validCount(entry.request_count);
    if (entry.request_latency_distribution) {
      addDistribution(group.counts, entry.request_latency_distribution);
    }
    groups.set(key, group);
  }
  return groups;
}

/** Builds API-level stability from per-interval latency histograms. Users are
 * deliberately combined before the per-interval p95 eligibility check. */
export function buildLatencyStabilitySummaries(
  intervals: WorkloadMetricsInterval[],
): LatencyStabilitySummary[] {
  const groups = new Map<string, MutableGroup>();
  const hasDroppedRecords = intervals.some((interval) => interval.dropped_records > 0);

  for (const interval of intervals) {
    for (const [key, intervalGroup] of intervalGroups(interval.entries)) {
      const group = groups.get(key) ?? {
        sample: intervalGroup.sample,
        totalRequests: 0,
        measuredRequests: 0,
        activeIntervals: 0,
        eligibleIntervals: 0,
        censoredIntervals: 0,
        counts: emptyDistribution(),
        points: [],
      };
      group.totalRequests += intervalGroup.totalRequests;
      if (intervalGroup.totalRequests > 0) group.activeIntervals += 1;
      const intervalMeasuredRequests = distributionTotal(intervalGroup.counts);
      group.measuredRequests += intervalMeasuredRequests;
      addDistribution(group.counts, intervalGroup.counts);

      if (intervalMeasuredRequests >= MIN_STABILITY_REQUESTS_PER_INTERVAL) {
        const p95 = estimateHistogramQuantile(intervalGroup.counts, 0.95);
        if (p95) {
          const p50 = estimateHistogramQuantile(intervalGroup.counts, 0.5);
          group.eligibleIntervals += 1;
          if (p95.censored) group.censoredIntervals += 1;
          group.points.push({
            timestamp: interval.start_ms,
            p50Ms: p50?.value ?? p95.value,
            p50Censored: p50?.censored ?? false,
            p95Ms: p95.value,
            censored: p95.censored,
            measuredRequests: intervalMeasuredRequests,
          });
        }
      }
      groups.set(key, group);
    }
  }

  return Array.from(groups, ([key, group]) => {
    const p10 = percentilePoint(group.points, 0.1);
    const typical = percentilePoint(group.points, 0.5);
    const p90 = percentilePoint(group.points, 0.9);
    const spread = p10 && p90 && p10.value > 0 ? p90.value / p10.value : null;
    const spreadIsLowerBound = Boolean(p90?.censored && !p10?.censored);
    const spreadIsUnknown = Boolean(p10?.censored);
    const usableSpread = spreadIsUnknown ? null : spread;
    // Same p10/p90 temporal spread, but over each interval's median instead of
    // its tail, so a drifting typical request is caught even when p95 holds.
    const medianP10 = percentilePoint(group.points, 0.1, 'p50Ms');
    const medianP90 = percentilePoint(group.points, 0.9, 'p50Ms');
    const medianSpreadRaw = medianP10 && medianP90 && medianP10.value > 0
      ? medianP90.value / medianP10.value
      : null;
    const medianSpreadIsLowerBound = Boolean(medianP90?.censored && !medianP10?.censored);
    const medianSpread = medianP10?.censored ? null : medianSpreadRaw;
    const windowP50 = estimateHistogramQuantile(group.counts, 0.5);
    const windowP95 = estimateHistogramQuantile(group.counts, 0.95);
    const tailSpread = windowP50 && windowP95 && windowP50.value > 0
      ? windowP95.value / windowP50.value
      : null;
    const slowCount = (['1000_to_2500_ms', '2500_to_5000_ms', 'gte_5000_ms'] as LatencyBucketKey[])
      .reduce((total, bucket) => total + validCount(group.counts[bucket]), 0);
    const eligibleIntervalCoveragePercent = group.activeIntervals === 0
      ? 0
      : group.eligibleIntervals / group.activeIntervals * 100;
    const histogramCoveragePercent = group.totalRequests === 0
      ? 0
      : Math.min(100, group.measuredRequests / group.totalRequests * 100);
    const tailRating = ratingFor(
      usableSpread,
      spreadIsLowerBound,
      group.eligibleIntervals,
      group.measuredRequests,
    );
    const medianRating = ratingFor(
      medianSpread,
      medianSpreadIsLowerBound,
      group.eligibleIntervals,
      group.measuredRequests,
    );
    // Whole-profile stability: an API is only as stable as its least stable
    // percentile. The driver records which percentile(s) pushed it off stable.
    const rating = RATING_ORDER[medianRating] > RATING_ORDER[tailRating]
      ? medianRating
      : tailRating;
    const spreadDriver: LatencyStabilityDriver = medianRating === tailRating
      ? 'both'
      : RATING_ORDER[medianRating] > RATING_ORDER[tailRating] ? 'median' : 'tail';
    const sample = group.sample;

    return {
      key,
      label: [sample.resource_type, sample.action.toLowerCase(), sample.api_label].join(' · '),
      resourceType: sample.resource_type || 'Unknown',
      action: sample.action.toLowerCase() || 'Unknown',
      apiLabel: sample.api_label || 'Unknown',
      rating,
      confidence: confidenceFor(
        rating,
        group.eligibleIntervals,
        group.measuredRequests,
        eligibleIntervalCoveragePercent,
        histogramCoveragePercent,
        hasDroppedRecords,
      ),
      spreadDriver,
      typicalP95Ms: typical?.value ?? null,
      typicalP95Censored: typical?.censored ?? false,
      p10P95Ms: p10?.value ?? null,
      p90P95Ms: p90?.value ?? null,
      temporalSpreadRatio: usableSpread,
      temporalSpreadIsLowerBound: spreadIsLowerBound,
      medianSpreadRatio: medianSpread,
      typicalP50Ms: percentilePoint(group.points, 0.5, 'p50Ms')?.value ?? null,
      windowP50Ms: windowP50?.value ?? null,
      windowP95Ms: windowP95?.value ?? null,
      windowP95Censored: windowP95?.censored ?? false,
      tailSpreadRatio: tailSpread,
      measuredRequests: group.measuredRequests,
      totalRequests: group.totalRequests,
      histogramCoveragePercent,
      activeIntervals: group.activeIntervals,
      eligibleIntervals: group.eligibleIntervals,
      eligibleIntervalCoveragePercent,
      censoredIntervals: group.censoredIntervals,
      slowerThan1000Percent: group.measuredRequests === 0
        ? null
        : slowCount / group.measuredRequests * 100,
      points: group.points.sort((left, right) => left.timestamp - right.timestamp),
    };
  }).sort((left, right) => left.label.localeCompare(right.label));
}

/** Re-aggregates the selected workload rows inside each interval before
 * calculating percentiles. Histogram percentiles are not additive, so a
 * combined result cannot be derived from the already summarized rows. */
export function buildCombinedLatencyStabilitySummary(
  intervals: WorkloadMetricsInterval[],
  selectedKeys: ReadonlySet<string>,
): LatencyStabilitySummary | null {
  if (selectedKeys.size === 0) return null;

  const combinedIntervals = intervals.map((interval) => {
    const selectedEntries = interval.entries.filter((entry) => selectedKeys.has(entryKey(entry)));
    if (selectedEntries.length === 0) return { ...interval, entries: [] };

    const counts = emptyDistribution();
    let hasDistribution = false;
    let requestCount = 0;
    let totalProcessingTimeNs = 0;
    for (const entry of selectedEntries) {
      requestCount += validCount(entry.request_count);
      totalProcessingTimeNs += validCount(entry.total_processing_time_ns);
      if (entry.request_latency_distribution) {
        hasDistribution = true;
        addDistribution(counts, entry.request_latency_distribution);
      }
    }

    const combinedEntry: WorkloadMetricsEntry = {
      username: '__combined__',
      resource_type: '__combined__',
      action: '__combined__',
      api_label: '__combined__',
      request_count: requestCount,
      total_processing_time_ns: totalProcessingTimeNs,
      ...(hasDistribution ? { request_latency_distribution: counts } : {}),
    };
    return { ...interval, entries: [combinedEntry] };
  });

  const summary = buildLatencyStabilitySummaries(combinedIntervals)[0];
  if (!summary) return null;
  const rowCount = selectedKeys.size;
  return {
    ...summary,
    key: '__combined_selection__',
    label: `Combined selection (${rowCount} ${rowCount === 1 ? 'row' : 'rows'})`,
    resourceType: 'Combined selection',
    action: '',
    apiLabel: '',
  };
}
